import { useState } from "react";
import {
  COMPANIES,
  DISTRIBUTORS,
} from "../../constants/companyDistributorData";

export const initialParentForm = {
  parentCode: "",
  parentName: "",
  description: "",
  status: "active",
};

export const initialChildForm = {
  childCode: "",
  childName: "",
  parentCode: "",
  description: "",
  status: "active",
};

export const useParentCodeData = () => {
  const [parents, setParents] = useState([...COMPANIES]);

  const [children, setChildren] = useState([...DISTRIBUTORS]);

  // ── Parent modal (create + edit) ───────────────────────────────────────────
  const [parentModal, setParentModal] = useState(false);
  const [editingParent, setEditingParent] = useState(null); // original parentCode being edited
  const [parentForm, setParentForm] = useState(initialParentForm);
  const [parentErrors, setParentErrors] = useState({});

  const openParentModal = () => {
    setEditingParent(null);
    setParentForm(initialParentForm);
    setParentErrors({});
    setParentModal(true);
  };

  const openEditParentModal = (parent) => {
    setEditingParent(parent.parentCode);
    setParentForm({ ...parent });
    setParentErrors({});
    setParentModal(true);
  };

  const closeParentModal = () => setParentModal(false);

  const handleParentChange = (e) => {
    const { name, value } = e.target;
    setParentForm((prev) => ({ ...prev, [name]: value }));
    if (parentErrors[name])
      setParentErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
  };

  const validateParent = () => {
    const errs = {};
    if (!parentForm.parentCode.trim())
      errs.parentCode = "Parent code is required";
    if (!parentForm.parentName.trim())
      errs.parentName = "Parent name is required";
    // On create: check duplicate. On edit: allow same code for the same record
    const isDuplicate = parents.some(
      (p) =>
        p.parentCode.toLowerCase() ===
          parentForm.parentCode.trim().toLowerCase() &&
        p.parentCode !== editingParent,
    );
    if (isDuplicate) errs.parentCode = "Parent code already exists";
    return errs;
  };

  const submitParent = () => {
    const errs = validateParent();
    if (Object.keys(errs).length) {
      setParentErrors(errs);
      return;
    }
    if (editingParent) {
      setParents((prev) =>
        prev.map((p) =>
          p.parentCode === editingParent
            ? { ...parentForm, parentCode: parentForm.parentCode.trim() }
            : p,
        ),
      );
    } else {
      setParents((prev) => [
        ...prev,
        { ...parentForm, parentCode: parentForm.parentCode.trim() },
      ]);
    }
    closeParentModal();
  };

  // ── Child modal (create + edit) ────────────────────────────────────────────
  const [childModal, setChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState(null); // original childCode being edited
  const [childForm, setChildForm] = useState(initialChildForm);
  const [childErrors, setChildErrors] = useState({});

  const openChildModal = () => {
    setEditingChild(null);
    setChildForm(initialChildForm);
    setChildErrors({});
    setChildModal(true);
  };

  const openEditChildModal = (child) => {
    setEditingChild(child.childCode);
    setChildForm({ ...child });
    setChildErrors({});
    setChildModal(true);
  };

  const closeChildModal = () => setChildModal(false);

  const handleChildChange = (e) => {
    const { name, value } = e.target;
    setChildForm((prev) => ({ ...prev, [name]: value }));
    if (childErrors[name])
      setChildErrors((prev) => {
        const e = { ...prev };
        delete e[name];
        return e;
      });
  };

  const validateChild = () => {
    const errs = {};
    if (!childForm.parentCode) errs.parentCode = "Parent code is required";
    if (!childForm.childCode.trim()) errs.childCode = "Child code is required";
    if (!childForm.childName.trim()) errs.childName = "Child name is required";
    const isDuplicate = children.some(
      (c) =>
        c.childCode.toLowerCase() ===
          childForm.childCode.trim().toLowerCase() &&
        c.childCode !== editingChild,
    );
    if (isDuplicate) errs.childCode = "Child code already exists";
    return errs;
  };

  const submitChild = () => {
    const errs = validateChild();
    if (Object.keys(errs).length) {
      setChildErrors(errs);
      return;
    }
    if (editingChild) {
      setChildren((prev) =>
        prev.map((c) =>
          c.childCode === editingChild
            ? { ...childForm, childCode: childForm.childCode.trim() }
            : c,
        ),
      );
    } else {
      setChildren((prev) => [
        ...prev,
        { ...childForm, childCode: childForm.childCode.trim() },
      ]);
    }
    closeChildModal();
  };

  // ── Selected parent filter ─────────────────────────────────────────────────
  const [selectedParent, setSelectedParent] = useState(null);

  const visibleChildren = selectedParent
    ? children.filter((c) => c.parentCode === selectedParent)
    : children;

  return {
    parents,
    children: visibleChildren,
    allChildren: children,
    selectedParent,
    setSelectedParent,
    parentModal,
    editingParent,
    openParentModal,
    openEditParentModal,
    closeParentModal,
    parentForm,
    parentErrors,
    handleParentChange,
    submitParent,
    childModal,
    editingChild,
    openChildModal,
    openEditChildModal,
    closeChildModal,
    childForm,
    childErrors,
    handleChildChange,
    submitChild,
  };
};
